/**
 * A6 — „Gruppe erstellen" (v1.8 WS-Plattform, D-111/D-132). WS-Admin gibt einen
 * Namen an und weist optional Workspace-Mitglieder zu. Beim Erstellen legt das
 * Backend (POST /groups) automatisch den Root-Ordner `Teams/<Gruppe>` (+
 * group→editor-Grant), den kanonischen KI-Ordner (D-134) und den Gruppen-
 * eigenen Papierkorb/Archiv (D-132) an; der Ersteller wird Gruppen-Admin. Die
 * zugewiesenen Mitglieder werden anschließend per PUT /groups/:id/members
 * hinzugefügt.
 *
 * Die im Mockup genannte Autogen departmentaler Views/Forms hängt an der noch
 * nicht gebauten Paket-/Views-Plattform (D-134) — der Hinweistext bleibt als
 * verbindliche Zusage stehen, die Views entstehen mit dem Views-Backend.
 */

import { useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

import type { RyfeGroup, WsMember } from "../api";

const initials = (email: string) =>
  email.replace(/@.*/, "").slice(0, 2).toUpperCase();

export const CreateGroupDialog = ({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (group: RyfeGroup) => void;
}) => {
  const { workspace, user, reloadGroups } = useRyfe();
  const [name, setName] = useState("");
  const [members, setMembers] = useState<WsMember[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!workspace) {
      return;
    }
    let cancelled = false;
    ryfeApi
      .listMembers(workspace.id)
      .then((r) => {
        if (!cancelled) {
          // The creator becomes the group-admin automatically — drop self from
          // the assignable pool to avoid a redundant self-add.
          setMembers(r.members.filter((m) => m.id !== user?.id));
        }
      })
      .catch(() => {
        /* a plain listing failure just leaves the assign pool empty */
      });
    return () => {
      cancelled = true;
    };
  }, [workspace, user]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const submit = async () => {
    if (!workspace || !name.trim() || busy) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      const group = await ryfeApi.createGroup(workspace.id, name.trim());
      // Assign the picked members (best-effort each; the group already exists).
      for (const m of members) {
        if (selected.has(m.id)) {
          try {
            await ryfeApi.addGroupMember(workspace.id, group.id, m.email);
          } catch {
            /* one failed assignment must not abort the others */
          }
        }
      }
      await reloadGroups();
      onCreated(group);
    } catch (e) {
      const msg = (e as { status?: number })?.status === 409;
      setError(
        msg
          ? "Eine Gruppe mit diesem Namen existiert bereits."
          : "Anlegen fehlgeschlagen.",
      );
      setBusy(false);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Gruppe erstellen"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <div className="ryfe-panel__title" style={{ margin: 0 }}>
          Gruppe erstellen
        </div>

        <div className="ryfe-field-label">Name</div>
        <input
          className="ryfe-field-input"
          value={name}
          autoFocus
          placeholder="z. B. Vertrieb"
          data-testid="group-name"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              submit();
            }
          }}
        />

        <div className="ryfe-field-label">Mitglieder zuweisen</div>
        <div className="ryfe-chiprow">
          {members.length === 0 && (
            <span className="ryfe-muted" style={{ padding: 0 }}>
              Keine weiteren Workspace-Mitglieder.
            </span>
          )}
          {members.map((m) => (
            <button
              key={m.id}
              type="button"
              title={m.email}
              className={`ryfe-chip${
                selected.has(m.id) ? " ryfe-chip--on" : ""
              }`}
              onClick={() => toggle(m.id)}
            >
              {initials(m.email)}
              {selected.has(m.id) ? " ✓" : ""}
            </button>
          ))}
        </div>

        <div className="ryfe-notice">
          Autogen beim Erstellen: Root-Ordner{" "}
          <code>Teams/{name || "<Gruppe>"}</code> + Auto-Rechte (group→editor),
          kanonischer KI-Ordner der Gruppe, eigener Papierkorb/Archiv.
          Departmentale Views + Forms folgen mit der Views-Plattform.
        </div>

        {error && <div className="ryfe-notice ryfe-notice--warn">{error}</div>}

        <div className="ryfe-modal__actions">
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <span style={{ flex: 1 }} />
          <button
            className="ryfe-btn ryfe-btn--primary"
            disabled={!name.trim() || busy}
            data-testid="group-create"
            onClick={submit}
          >
            Gruppe erstellen
          </button>
        </div>
      </div>
    </div>
  );
};
